import { logger } from './logger';
import { secureStorage } from './secure-storage';
import type { Gtag } from '../types/analytics';

// Google Analytics 4 Configuration from environment
const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-FR18ETJFB7';
const GA_STREAM_ID = import.meta.env.VITE_GA_STREAM_ID || '11479643779';

// Check if we're in production
const isProduction = import.meta.env.MODE === 'production';

// Check if user has consented to analytics
const hasAnalyticsConsent = () => {
  // Check localStorage directly for analytics consent
  // as it's not a secure storage key
  const consent = localStorage.getItem('analytics_consent');
  return consent === 'granted';
};

// Sanitize data to remove PII
const sanitizeData = (data: any): any => {
  if (!data) return data;
  
  const piiPatterns = {
    email: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
    phone: /(\+?\d{1,3}[-.\s]?)?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}/g,
    creditCard: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
    ssn: /\b\d{3}-\d{2}-\d{4}\b/g,
  };

  const sanitize = (obj: any): any => {
    if (typeof obj === 'string') {
      let sanitized = obj;
      Object.values(piiPatterns).forEach(pattern => {
        sanitized = sanitized.replace(pattern, '[REDACTED]');
      });
      return sanitized;
    }
    
    if (Array.isArray(obj)) {
      return obj.map(sanitize);
    }
    
    if (typeof obj === 'object' && obj !== null) {
      const result: any = {};
      for (const [key, value] of Object.entries(obj)) {
        // Skip sensitive fields
        if (['password', 'token', 'api_key', 'secret'].includes(key.toLowerCase())) {
          continue;
        }
        result[key] = sanitize(value);
      }
      return result;
    }
    
    return obj;
  };

  return sanitize(data);
};

// Analytics service class
class AnalyticsService {
  private initialized = false;
  private userId: string | null = null;
  private userProperties: Record<string, any> = {};
  private recentEvents: Map<string, number> = new Map();
  private dedupeTimeWindow = 100; // 100ms window for deduplication

  constructor() {
    this.initialize();
  }

  private initialize() {
    if (typeof window === 'undefined') {
      logger.warn('Analytics', 'Not available in server environment');
      return;
    }

    // Initialize dataLayer if not exists
    window.dataLayer = window.dataLayer || [];

    // Define gtag function if not exists
    if (!window.gtag) {
      window.gtag = function() {
        window.dataLayer!.push(arguments);
      } as Gtag.Gtag;
    }

    // Set default consent state
    this.updateConsent({
      analytics: hasAnalyticsConsent() ? 'granted' : 'denied',
      ads: 'denied' // Always deny ads cookies for privacy
    });

    this.initialized = true;
    logger.info('Analytics', 'Initialized with privacy protections');
  }

  // Push to dataLayer with privacy protection
  private pushToDataLayer(command: string, ...args: any[]) {
    if (!hasAnalyticsConsent() && command === 'event') {
      logger.info('Analytics', 'Event blocked - no consent');
      return;
    }
    
    // Sanitize all data before sending
    const sanitizedArgs = args.map(arg => sanitizeData(arg));
    
    // Push the command and arguments to dataLayer
    window.dataLayer.push([command, ...sanitizedArgs]);
    
    // Also call gtag if available - not needed as gtag uses dataLayer
  }

  // Set user ID for tracking
  setUserId(userId: string | null) {
    if (!this.initialized) return;

    this.userId = userId;
    
    if (userId) {
      window.gtag('config', GA_MEASUREMENT_ID, {
        user_id: userId
      });
      logger.info('Analytics', 'User ID set', { userId });
    }
  }

  // Set user properties
  setUserProperties(properties: Record<string, any>) {
    if (!this.initialized || !window.gtag) return;

    window.gtag('set', {
      user_properties: properties
    });
    logger.info('Analytics', 'User properties set', properties);
  }

  // Track page views
  trackPageView(path: string, title?: string) {
    if (!this.initialized || !window.gtag) return;

    const pageTitle = title || document.title;
    const parameters = {
      page_path: path,
      page_title: pageTitle,
      page_location: window.location.href
    };
    
    // Use the generic trackEvent method to get deduplication
    this.trackEvent('page_view', parameters);
  }

  // Track custom events
  trackEvent(eventName: string, parameters?: Record<string, any>) {
    if (!this.initialized || !window.gtag) return;

    // Create a unique key for deduplication
    const eventKey = `${eventName}-${JSON.stringify(parameters || {})}`;
    const now = Date.now();
    const lastTracked = this.recentEvents.get(eventKey);
    
    // Skip if this exact event was tracked within the dedupe window
    if (lastTracked && (now - lastTracked) < this.dedupeTimeWindow) {
      logger.info('Analytics', 'Duplicate event skipped', { eventName, parameters });
      return;
    }
    
    // Track the event
    this.recentEvents.set(eventKey, now);
    window.gtag('event', eventName, parameters);
    logger.info('Analytics', 'Event tracked', { eventName, parameters });
    
    // Clean up old entries to prevent memory leak
    if (this.recentEvents.size > 100) {
      const cutoffTime = now - this.dedupeTimeWindow;
      for (const [key, time] of this.recentEvents.entries()) {
        if (time < cutoffTime) {
          this.recentEvents.delete(key);
        }
      }
    }
  }

  // Authentication events
  trackSignUp(method: string = 'email') {
    this.trackEvent('sign_up', { method });
  }

  trackLogin(method: string = 'email') {
    this.trackEvent('login', { method });
  }

  trackEmailVerified() {
    this.trackEvent('email_verified');
  }

  // Ecommerce events
  trackBeginCheckout(
    value: number,
    currency: string,
    items: Array<{
      item_id: string;
      item_name: string;
      price: number;
      quantity?: number;
    }>
  ) {
    this.trackEvent('begin_checkout', {
      value,
      currency,
      items,
    });
  }

  trackAddPaymentInfo(paymentType: string) {
    this.trackEvent('add_payment_info', {
      payment_type: paymentType,
    });
  }

  trackPurchase(
    transactionId: string,
    value: number,
    currency: string,
    items: Array<{
      item_id: string;
      item_name: string;
      price: number;
      quantity?: number;
      item_category?: string;
    }>,
    tax?: number
  ) {
    this.trackEvent('purchase', {
      transaction_id: transactionId,
      value,
      currency,
      tax,
      items,
    });
  }

  // Service events
  trackServiceActivated(
    serviceId: string,
    productId: string,
    productName: string,
    value: number
  ) {
    this.trackEvent('service_activated', {
      service_id: serviceId,
      product_id: productId,
      product_name: productName,
      value,
    });
  }

  trackPlanChanged(
    serviceId: string,
    fromPlan: string,
    toPlan: string,
    valueDifference: number
  ) {
    this.trackEvent('plan_changed', {
      service_id: serviceId,
      from_plan: fromPlan,
      to_plan: toPlan,
      value_difference: valueDifference,
    });
  }

  trackServiceRenewed(
    serviceId: string,
    productId: string,
    value: number,
    period: number
  ) {
    this.trackEvent('service_renewed', {
      service_id: serviceId,
      product_id: productId,
      value,
      renewal_period: period,
    });
  }

  // Feature usage events
  trackFeatureUsed(featureName: string, metadata?: Record<string, any>) {
    this.trackEvent('feature_used', {
      feature_name: featureName,
      ...metadata,
    });
  }

  // Error tracking
  trackError(error: string, fatal: boolean = false) {
    this.trackEvent('exception', {
      description: error,
      fatal,
    });
  }

  // Search tracking
  trackSearch(searchTerm: string, resultsCount?: number) {
    this.trackEvent('search', {
      search_term: searchTerm,
      results_count: resultsCount,
    });
  }

  // Content engagement
  trackContentView(contentType: string, contentId: string, contentName?: string) {
    // Include all parameters in the deduplication check
    this.trackEvent('select_content', {
      content_type: contentType,
      content_id: contentId,
      content_name: contentName,
    });
  }

  // File operations
  trackFileOperation(
    operation: 'upload' | 'download' | 'delete',
    fileType: string,
    fileSize?: number
  ) {
    this.trackEvent('file_operation', {
      operation,
      file_type: fileType,
      file_size: fileSize,
    });
  }

  // Service configuration
  trackServiceConfiguration(
    serviceType: 'nfs' | 'samba' | 'vpn' | 'backup' | 'subuser',
    action: string,
    success: boolean
  ) {
    this.trackEvent('service_configuration', {
      service_type: serviceType,
      action,
      success,
    });
  }

  // Dashboard interactions
  trackDashboardAction(action: string, section?: string) {
    this.trackEvent('dashboard_action', {
      action,
      section,
    });
  }

  // Support interactions
  trackSupportRequest(requestType: string, category?: string) {
    this.trackEvent('support_request', {
      request_type: requestType,
      category,
    });
  }

  // Timing events
  trackTiming(category: string, variable: string, value: number, label?: string) {
    this.trackEvent('timing_complete', {
      name: variable,
      value,
      event_category: category,
      event_label: label,
    });
  }

  // Session timing
  trackSessionDuration(duration: number) {
    this.trackEvent('session_duration', {
      engagement_time_msec: duration,
    });
  }

  // Consent management
  updateConsent(consentSettings: {
    analytics?: 'granted' | 'denied';
    ads?: 'granted' | 'denied';
  }) {
    if (!window.gtag) return;

    window.gtag('consent', 'update', {
      analytics_storage: consentSettings.analytics || 'granted',
      ad_storage: consentSettings.ads || 'denied'
    });
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

// Export specific tracking functions for convenience
export const {
  setUserId,
  setUserProperties,
  trackPageView,
  trackEvent,
  trackSignUp,
  trackLogin,
  trackEmailVerified,
  trackBeginCheckout,
  trackAddPaymentInfo,
  trackPurchase,
  trackServiceActivated,
  trackPlanChanged,
  trackServiceRenewed,
  trackFeatureUsed,
  trackError,
  trackSearch,
  trackContentView,
  trackFileOperation,
  trackServiceConfiguration,
  trackDashboardAction,
  trackSupportRequest,
  trackTiming,
  trackSessionDuration,
  updateConsent,
} = analytics;